import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import AdminLayout from "@/components/layout/AdminLayout";
import ImageUploadField from "@/components/ImageUploadField";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { normalizeImageUrl } from "@/lib/imageUrl";
import {
  Building2, ArrowLeft, Save, Archive, RotateCcw, Trash2, CheckCircle2, Star, Plus, X,
  Users, Users2, ClipboardList, Calendar, Trophy, BarChart3, CreditCard, ShieldCheck, Target, Activity, Image as ImageIcon,
} from "lucide-react";

const PROVINCES = ["AB", "BC", "MB", "NB", "NL", "NS", "NT", "NU", "ON", "PE", "QC", "SK", "YT"];
const ORG_TYPES = ["club", "academy", "association", "cbr_nation"];
const inp = "w-full px-3 py-2 border border-slate-300 rounded-sm text-sm";
const EMPTY_TEAM = { name: "", age_division: "", league: "", league_id: "", league_name: "", province: "" };

const TABS = [
  { id: "overview", label: "Command Center", icon: LayoutGridIcon },
  { id: "details", label: "Details", icon: Building2 },
  { id: "teams", label: "Teams", icon: Users2 },
  { id: "statistics", label: "Statistics", icon: BarChart3 },
];
function LayoutGridIcon(props) { return <Target {...props} />; }

export default function AdminOrganizationDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "super_admin";

  const [org, setOrg] = useState(null);
  const [teams, setTeams] = useState([]);
  const [stats, setStats] = useState(null);
  const [form, setForm] = useState({});
  const [tab, setTab] = useState("overview");
  const [saving, setSaving] = useState(false);
  const [teamModal, setTeamModal] = useState(false);
  const [teamForm, setTeamForm] = useState(EMPTY_TEAM);
  const [leagues, setLeagues] = useState([]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/live/organizations/${id}`);
      setOrg(data.organization); setTeams(data.teams || []); setStats(data.stats || null);
      setForm(data.organization);
    } catch (e) { toast.error("Organization not found"); navigate("/admin/live/organizations"); }
  }, [id, navigate]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/live/leagues", { params: { active: true } }).then(({ data }) => setLeagues(data)).catch(() => {}); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      const keys = ["name", "type", "website", "province", "city", "description", "logo_url", "primary_color", "secondary_color",
       "primary_contact", "email", "phone", "instagram", "x_handle", "facebook", "youtube",
       "home_fields", "founded_year", "league_affiliations", "age_divisions", "verified", "founding_member"];
      keys.forEach((k) => {
        let v = form[k];
        if (v === undefined || v === null) return;
        if (k === "name" && !String(v).trim()) return;
        if (k === "founded_year") { if (v === "" || v === null) return; v = Number(v); }
        payload[k] = v;
      });
      const { data } = await api.patch(`/admin/live/organizations/${id}`, payload);
      setOrg(data); toast.success("Organization saved"); await load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Save failed"); }
    finally { setSaving(false); }
  };

  const archive = async (archived) => {
    try { await api.post(`/admin/live/organizations/${id}/archive`, { archived }); toast.success(archived ? "Archived" : "Restored"); load(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Failed"); }
  };
  const remove = async () => {
    if (!window.confirm(`Permanently delete "${org.name}"? Super-admin only. This cannot be undone.`)) return;
    try { await api.delete(`/admin/live/organizations/${id}`); toast.success("Organization deleted"); navigate("/admin/live/organizations"); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Failed"); }
  };
  const createTeam = async () => {
    if (!teamForm.name.trim()) { toast.error("Team name required"); return; }
    try { await api.post(`/admin/live/organizations/${id}/teams`, teamForm); toast.success("Team created"); setTeamModal(false); setTeamForm(EMPTY_TEAM); load(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Failed"); }
  };

  if (!org) return <AdminLayout><div className="p-8 text-sm text-slate-400">Loading…</div></AdminLayout>;

  return (
    <AdminLayout>
      <div className="p-8 max-w-5xl">
        <Link to="/admin/live/organizations" className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-[#E63946] uppercase tracking-wider font-semibold"><ArrowLeft className="w-3.5 h-3.5" /> All Organizations</Link>

        {/* Header */}
        <div className="mt-4 bg-white border border-slate-200 rounded-sm p-5 flex items-start gap-4 flex-wrap">
          <div className="w-16 h-16 shrink-0 rounded-sm border border-slate-200 bg-slate-50 grid place-items-center overflow-hidden">
            {org.logo_url ? <img src={normalizeImageUrl(org.logo_url)} alt="" className="w-full h-full object-contain" /> : <Building2 className="w-7 h-7 text-slate-300" />}
          </div>
          <div className="flex-1 min-w-[200px]">
            <h1 data-testid="org-detail-name" className="font-display text-3xl uppercase tracking-tighter">{org.name}</h1>
            <div className="flex flex-wrap gap-1.5 mt-2">
              <span className="text-[9px] font-bold uppercase bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded-sm">{org.type === "cbr_nation" ? "CBR Nation" : org.type}</span>
              {org.province && <span className="text-[9px] font-bold uppercase bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded-sm">{org.province}</span>}
              {org.verified && <span className="text-[9px] font-bold uppercase bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-sm inline-flex items-center gap-0.5"><CheckCircle2 className="w-2.5 h-2.5" /> Verified</span>}
              {org.founding_member && <span className="text-[9px] font-bold uppercase bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-sm inline-flex items-center gap-0.5"><Star className="w-2.5 h-2.5" /> Founding</span>}
              <span data-testid="org-claim-status" className="text-[9px] font-bold uppercase bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded-sm">{org.owner_user_id ? "Claimed" : "Unclaimed"}</span>
              {org.archived && <span className="text-[9px] font-bold uppercase bg-slate-700 text-white px-1.5 py-0.5 rounded-sm">Archived</span>}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {org.archived
              ? <button data-testid="org-detail-restore" onClick={() => archive(false)} className="inline-flex items-center gap-1 px-3 py-2 text-[10px] font-semibold uppercase tracking-wider border border-slate-300 rounded-sm"><RotateCcw className="w-3.5 h-3.5" /> Restore</button>
              : <button data-testid="org-detail-archive" onClick={() => archive(true)} className="inline-flex items-center gap-1 px-3 py-2 text-[10px] font-semibold uppercase tracking-wider border border-slate-300 rounded-sm"><Archive className="w-3.5 h-3.5" /> Archive</button>}
            {isSuperAdmin && <button data-testid="org-detail-delete" onClick={remove} className="inline-flex items-center gap-1 px-3 py-2 text-[10px] font-semibold uppercase tracking-wider bg-[#E63946] text-white rounded-sm"><Trash2 className="w-3.5 h-3.5" /> Delete</button>}
          </div>
        </div>

        {/* Tabs */}
        <div className="mt-6 flex gap-1 border-b border-slate-200 overflow-x-auto">
          {TABS.map((t) => (
            <button key={t.id} data-testid={`org-tab-${t.id}`} onClick={() => setTab(t.id)}
              className={`inline-flex items-center gap-1.5 px-4 py-2.5 text-xs font-semibold uppercase tracking-wider border-b-2 -mb-px whitespace-nowrap ${tab === t.id ? "border-[#E63946] text-[#E63946]" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
              <t.icon className="w-3.5 h-3.5" /> {t.label}
            </button>
          ))}
        </div>

        {/* Overview / Command center */}
        {tab === "overview" && (
          <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="org-command-center">
            <CmdCard icon={Users2} label="Teams" value={stats?.team_count ?? 0} onClick={() => setTab("teams")} />
            <CmdCard icon={Users} label="Players" value={stats?.player_count ?? 0} onClick={() => setTab("statistics")} />
            <CmdCard icon={BarChart3} label="Statistics" hint="View" onClick={() => setTab("statistics")} />
            <CmdCard icon={ShieldCheck} label="Claim Status" hint={org.owner_user_id ? "Claimed" : "Unclaimed"} />
            <CmdCard icon={Building2} label="Details & Branding" hint="Edit" onClick={() => setTab("details")} />
            <CmdCard icon={ClipboardList} label="Staff" hint="Coming soon" soon />
            <CmdCard icon={Calendar} label="Schedule" hint="Coming soon" soon />
            <CmdCard icon={Trophy} label="Rankings" hint="Coming soon" soon />
            <CmdCard icon={CreditCard} label="Membership" hint="Coming soon" soon />
            <CmdCard icon={Target} label="Recruiter Activity" hint="Coming soon" soon />
            <CmdCard icon={Activity} label="Analytics" hint="Coming soon" soon />
          </div>
        )}

        {/* Details */}
        {tab === "details" && (
          <div className="mt-6 space-y-6" data-testid="org-details-form">
            <Section title="General">
              <F label="Organization Name *"><input data-testid="org-field-name" value={form.name || ""} onChange={(e) => set("name", e.target.value)} className={inp} /></F>
              <F label="Type"><select value={form.type || "club"} onChange={(e) => set("type", e.target.value)} className={`${inp} bg-white`}>{ORG_TYPES.map((t) => <option key={t}>{t}</option>)}</select></F>
              <F label="Website"><input value={form.website || ""} onChange={(e) => set("website", e.target.value)} className={inp} /></F>
              <F label="Province"><select value={form.province || ""} onChange={(e) => set("province", e.target.value)} className={`${inp} bg-white`}><option value="">—</option>{PROVINCES.map((p) => <option key={p}>{p}</option>)}</select></F>
              <F label="City"><input value={form.city || ""} onChange={(e) => set("city", e.target.value)} className={inp} /></F>
              <F label="Description" full><textarea value={form.description || ""} onChange={(e) => set("description", e.target.value)} rows={3} className={inp} /></F>
            </Section>

            <Section title="Branding">
              <div className="sm:col-span-2"><ImageUploadField label="Organization Logo" testid="org-logo-upload" value={form.logo_url ? normalizeImageUrl(form.logo_url) : ""} onChange={(v) => set("logo_url", v)} aspect="aspect-square" /></div>
              <F label="Primary Color"><ColorField value={form.primary_color} onChange={(v) => set("primary_color", v)} testid="org-primary-color" /></F>
              <F label="Secondary Color"><ColorField value={form.secondary_color} onChange={(v) => set("secondary_color", v)} testid="org-secondary-color" /></F>
            </Section>

            <Section title="Contact">
              <F label="Primary Contact"><input value={form.primary_contact || ""} onChange={(e) => set("primary_contact", e.target.value)} className={inp} /></F>
              <F label="Email"><input value={form.email || ""} onChange={(e) => set("email", e.target.value)} className={inp} /></F>
              <F label="Phone"><input value={form.phone || ""} onChange={(e) => set("phone", e.target.value)} className={inp} /></F>
            </Section>

            <Section title="Social Media">
              <F label="Instagram"><input value={form.instagram || ""} onChange={(e) => set("instagram", e.target.value)} className={inp} /></F>
              <F label="X (Twitter)"><input value={form.x_handle || ""} onChange={(e) => set("x_handle", e.target.value)} className={inp} /></F>
              <F label="Facebook"><input value={form.facebook || ""} onChange={(e) => set("facebook", e.target.value)} className={inp} /></F>
              <F label="YouTube"><input value={form.youtube || ""} onChange={(e) => set("youtube", e.target.value)} className={inp} /></F>
            </Section>

            <Section title="Baseball Information">
              <F label="Home Fields"><input value={form.home_fields || ""} onChange={(e) => set("home_fields", e.target.value)} className={inp} /></F>
              <F label="Founded Year"><input type="number" value={form.founded_year || ""} onChange={(e) => set("founded_year", e.target.value)} className={inp} /></F>
              <F label="League Affiliations"><input value={form.league_affiliations || ""} onChange={(e) => set("league_affiliations", e.target.value)} className={inp} /></F>
              <F label="Age Divisions"><input value={form.age_divisions || ""} onChange={(e) => set("age_divisions", e.target.value)} className={inp} /></F>
              <div className="sm:col-span-2 flex items-center gap-6 pt-2">
                <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={!!form.verified} onChange={(e) => set("verified", e.target.checked)} /> Verified</label>
                <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={!!form.founding_member} onChange={(e) => set("founding_member", e.target.checked)} /> Founding Member</label>
              </div>
            </Section>

            <div className="flex justify-end sticky bottom-4">
              <button data-testid="org-save-details" onClick={save} disabled={saving} className="inline-flex items-center gap-2 bg-[#E63946] text-white px-6 py-3 text-sm font-semibold uppercase tracking-wider rounded-sm shadow-lg disabled:opacity-60"><Save className="w-4 h-4" /> {saving ? "Saving…" : "Save Changes"}</button>
            </div>
          </div>
        )}

        {/* Teams */}
        {tab === "teams" && (
          <div className="mt-6" data-testid="org-teams-tab">
            <div className="flex items-center justify-between">
              <h3 className="font-display text-xl uppercase tracking-tight">Teams ({teams.length})</h3>
              <button data-testid="org-add-team-btn" onClick={() => { setTeamForm(EMPTY_TEAM); setTeamModal(true); }} className="inline-flex items-center gap-1.5 bg-[#0A192F] text-white px-3 py-2 text-xs font-semibold uppercase tracking-wider rounded-sm"><Plus className="w-4 h-4" /> Add Team</button>
            </div>
            <div className="mt-4 grid sm:grid-cols-2 gap-3">
              {teams.map((t) => (
                <div key={t.id} className="bg-white border border-slate-200 rounded-sm p-4 flex items-center gap-3">
                  <div className="w-10 h-10 shrink-0 rounded-sm border border-slate-100 bg-slate-50 grid place-items-center overflow-hidden">
                    {t.effective_logo_url ? <img src={normalizeImageUrl(t.effective_logo_url)} alt="" className="w-full h-full object-contain" /> : <ImageIcon className="w-4 h-4 text-slate-300" />}
                  </div>
                  <div className="flex-1"><div className="font-semibold text-sm">{t.name}</div><div className="text-[11px] text-slate-500">{t.age_division || "—"}{t.logo_inherited ? " · logo inherited" : ""}{t.archived ? " · archived" : ""}</div></div>
                  <Link to="/admin/live/teams" className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 hover:text-[#E63946]">Manage</Link>
                </div>
              ))}
              {teams.length === 0 && <div className="text-sm text-slate-400 py-8">No teams yet. Add the organization's first team.</div>}
            </div>
          </div>
        )}

        {/* Statistics */}
        {tab === "statistics" && stats && (
          <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="org-statistics">
            <Stat label="Active Teams" value={stats.team_count} />
            <Stat label="Registered Players" value={stats.player_count} />
            <Stat label="Org Rating" value={stats.cbr_org_rating ?? "—"} note="Rankings engine — coming soon" />
            <Stat label="National Rank" value={stats.national_rank ? `#${stats.national_rank}` : "—"} note="Coming soon" />
            <Stat label="Provincial Rank" value={stats.provincial_rank ? `#${stats.provincial_rank}` : "—"} note="Coming soon" />
            <Stat label="Championships" value={stats.championships} />
            <Stat label="College Commitments" value={stats.college_commitments} />
            <Stat label="MLB Draft Picks" value={stats.draft_picks} />
            <Stat label="Avg Team Rating" value={stats.avg_team_rating ?? "—"} />
            <Stat label="Winning %" value={stats.win_pct != null ? `${(stats.win_pct * 100).toFixed(1)}%` : "—"} note={`${stats.record.w}-${stats.record.l}-${stats.record.t}`} />
          </div>
        )}
      </div>

      {teamModal && (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4">
          <div className="bg-white rounded-sm w-full max-w-lg" data-testid="org-detail-team-modal">
            <div className="flex items-center justify-between p-5 border-b border-slate-200"><h3 className="font-display text-xl uppercase tracking-tight">Add Team</h3><button onClick={() => setTeamModal(false)} className="p-2 hover:bg-slate-100"><X className="w-5 h-5" /></button></div>
            <div className="p-5 grid sm:grid-cols-2 gap-3">
              <F label="Team Name *"><input data-testid="org-detail-team-name" value={teamForm.name} onChange={(e) => setTeamForm({ ...teamForm, name: e.target.value })} className={inp} /></F>
              <F label="Age Division"><input placeholder="e.g. 16U" value={teamForm.age_division} onChange={(e) => setTeamForm({ ...teamForm, age_division: e.target.value })} className={inp} /></F>
              <F label="League"><select data-testid="org-detail-team-league" value={teamForm.league_id} onChange={(e) => { const lg = leagues.find((l) => l.id === e.target.value); setTeamForm({ ...teamForm, league_id: e.target.value, league_name: lg ? (lg.abbreviation || lg.name) : "", league: lg ? (lg.abbreviation || lg.name) : "" }); }} className={`${inp} bg-white`}><option value="">— Select league —</option>{leagues.map((l) => <option key={l.id} value={l.id}>{l.abbreviation ? `${l.abbreviation} — ${l.name}` : l.name}</option>)}</select></F>
              <F label="Province"><select value={teamForm.province} onChange={(e) => setTeamForm({ ...teamForm, province: e.target.value })} className={`${inp} bg-white`}><option value="">—</option>{PROVINCES.map((p) => <option key={p}>{p}</option>)}</select></F>
            </div>
            <div className="p-5 border-t border-slate-200 flex justify-end gap-3"><button onClick={() => setTeamModal(false)} className="px-5 py-2 border border-slate-300 text-sm font-semibold uppercase tracking-wider rounded-sm">Cancel</button><button data-testid="org-detail-team-save" onClick={createTeam} className="px-5 py-2 bg-[#0A192F] text-white text-sm font-semibold uppercase tracking-wider rounded-sm">Create Team</button></div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

function Section({ title, children }) {
  return (
    <div className="bg-white border border-slate-200 rounded-sm p-5">
      <div className="label-overline text-[#E63946] mb-4">{title}</div>
      <div className="grid sm:grid-cols-2 gap-4">{children}</div>
    </div>
  );
}
function F({ label, children, full }) { return <div className={full ? "sm:col-span-2" : ""}><label className="label-overline text-[10px]">{label}</label><div className="mt-1">{children}</div></div>; }
function ColorField({ value, onChange, testid }) {
  return (
    <div className="flex items-center gap-2">
      <input type="color" value={value || "#0A192F"} onChange={(e) => onChange(e.target.value)} className="w-10 h-9 border border-slate-300 rounded-sm" />
      <input data-testid={testid} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="#RRGGBB" className={inp} />
    </div>
  );
}
function Stat({ label, value, note }) {
  return (
    <div className="bg-white border border-slate-200 rounded-sm p-4">
      <div className="label-overline text-[10px] text-slate-400">{label}</div>
      <div className="font-display text-3xl mt-1">{value}</div>
      {note && <div className="text-[10px] text-slate-400 mt-1">{note}</div>}
    </div>
  );
}
function CmdCard({ icon: Icon, label, value, hint, soon, onClick }) {
  return (
    <button disabled={soon || !onClick} onClick={onClick} data-testid={`org-cmd-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
      className={`text-left bg-white border border-slate-200 rounded-sm p-4 ${onClick && !soon ? "hover:border-[#E63946] hover:shadow-sm cursor-pointer" : "opacity-70"}`}>
      <Icon className="w-5 h-5 text-[#E63946]" />
      <div className="mt-3 font-semibold text-sm">{label}</div>
      {value !== undefined ? <div className="font-display text-2xl">{value}</div> : <div className="text-[11px] text-slate-400 uppercase tracking-wider">{hint}</div>}
    </button>
  );
}
